import { useRef, useState } from "react";
import { isNil } from "lodash";
import { useNavigate } from "react-router-dom";
import {
  Select,
  Table,
  Tag,
  Button,
  Tabs,
  Input as AntInput,
  notification,
} from "antd";
import { BiChevronLeft } from "react-icons/bi";
import {
  MdOutlineUploadFile,
  MdOutlineImage,
  MdOutlineLink,
} from "react-icons/md";
import { useTranslation } from "react-i18next";
import Card from "shared/components/Card";
import Header from "shared/components/Header";
import Spin from "shared/components/Spin";
import { useQuery, useMutation, useLazyQuery } from "shared/hooks/useApi";
import useScreenJob from "shared/hooks/useScreenJob";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { SCREENS } from "utils/constants";
import { createScreenDraft, readFileAsBase64 } from "utils/helpers/screenDraft";

const Create = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.SCREEN_STUDIO);
  const CREATE = t("CREATE", { returnObjects: true });
  const [processId, setProcessId] = useState(null);

  const { data: processData, loading: loadingProcesses } = useQuery(
    graphql.queries.PROCESSES,
    { variables: { take: 100, orderBy: [{ name: "ASC" }] } },
  );

  const processes = processData?.processes?.data || [];
  const selectedProcess = processes.find((p) => p.id === processId);
  const statuses = selectedProcess?.processStatuses || [];
  const sortedStatuses = [...statuses].sort((a, b) => a.order - b.order);

  if (loadingProcesses) return <Spin />;

  return (
    <>
      <Header
        title={CREATE.HEADER.TITLE}
        actions={[
          {
            text: CREATE.HEADER.BACK,
            onClick: () =>
              navigate(SCREENS.PRIVATE.ADMIN.SCREEN_STUDIOS.LIST.path),
            type: "gray--link",
            icon: <BiChevronLeft size={20} />,
          },
        ]}
      />
      <Card type="simple">
        <div className="m-bottom-16">
          <label className="input__label">{CREATE.PROCESS}</label>
          <Select
            className="screen-studio-create__select"
            placeholder={CREATE.PROCESS_PLACEHOLDER}
            value={processId}
            onChange={setProcessId}
            showSearch
            filterOption={(input, option) =>
              (option?.label || "")
                .toLowerCase()
                .indexOf(input.toLowerCase()) >= 0
            }
            options={processes.map((p) => ({ value: p.id, label: p.name }))}
          />
        </div>

        {processId && (
          <Tabs
            defaultActiveKey="import"
            items={[
              {
                key: "import",
                label: CREATE.TAB_IMPORT,
                children: (
                  <ImportTab statuses={sortedStatuses} navigate={navigate} />
                ),
              },
              {
                key: "manual",
                label: CREATE.TAB_MANUAL,
                children: (
                  <ManualTab statuses={sortedStatuses} navigate={navigate} />
                ),
              },
            ]}
          />
        )}
      </Card>
    </>
  );
};

/**
 * Creates a screen without Stitch: paste HTML and CSS.
 * CSS goes into styleContent, never into the HTML: the renderer injects it
 * for the whole version, so all its states share it.
 */
const ManualTab = ({ statuses, navigate }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.SCREEN_STUDIO);
  const CREATE = t("CREATE", { returnObjects: true });
  const [statusId, setStatusId] = useState(null);
  const [name, setName] = useState("");
  const [html, setHtml] = useState("");
  const [css, setCss] = useState("");
  const [instructions, setInstructions] = useState("");
  const [generatedStates, setGeneratedStates] = useState([]);
  const [creating, setCreating] = useState(false);
  const [generating, setGenerating] = useState(false);
  const fileInputRef = useRef(null);

  const [addScreen] = useMutation(graphql.mutations.ADD_SCREEN);
  const [addScreenVersion] = useMutation(graphql.mutations.ADD_SCREEN_VERSION);
  const [detectSlots] = useMutation(graphql.mutations.DETECT_SLOTS);
  const { generateHtml, generateState } = useScreenJob();

  /**
   * Fills the HTML and CSS fields from a mockup instead of creating the screen
   * straight away, so the result can be reviewed before anything is persisted.
   */
  const handleUpload = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file || generating) return;
    setGenerating(true);
    try {
      const data = await readFileAsBase64(file);
      const image = { data, mediaType: file.type };
      const note = instructions.trim() || undefined;
      const generated = await generateHtml({ image, instructions: note });
      if (!generated) return;
      setHtml(generated.html || "");
      setCss(generated.css || "");

      const drawn = [];
      let abandoned = false;
      const variants = generated.states || [];
      for (let index = 0; index < variants.length; index++) {
        const variant = variants[index];
        try {
          const state = await generateState({
            image,
            styleContent: generated.css,
            htmlContent: generated.html,
            conditionField: variant.conditionField || undefined,
            conditionValue: variant.conditionValue || undefined,
            instructions: note,
          });
          if (!state) {
            abandoned = true;
            break;
          }
          drawn.push({
            ...state,
            name: variant.name || state.name,
            order: isNil(variant.order) ? index + 1 : variant.order,
            conditions: variant.conditions || state.conditions,
          });
        } catch (_error) {}
      }
      if (!abandoned && variants.length && !drawn.length) {
        notification.error({ message: CREATE.GENERATE_ERROR });
      }
      setGeneratedStates(drawn);
    } catch (error) {
      notification.error({
        message: error.reason || CREATE.GENERATE_ERROR || error.message,
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleCreate = async () => {
    if (!statusId || !html.trim() || creating) return;
    setCreating(true);
    try {
      const { screenId } = await createScreenDraft({
        addScreen,
        addScreenVersion,
        detectSlots,
        statusId,
        description: name.trim() || CREATE.DEFAULT_NAME,
        htmlContent: html,
        styleContent: css,
        slots: [],
        states: generatedStates,
        copyStates: generatedStates.length > 0,
      });
      navigate(
        `${SCREENS.PRIVATE.ADMIN.SCREEN_STUDIOS.DETAIL.path}/${screenId}`,
      );
    } catch (error) {
      notification.error({
        message: CREATE.CREATE_ERROR || error.message,
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="screen-studio-create__manual">
      <div className="m-bottom-16">
        <label className="input__label">{CREATE.STATUS}</label>
        <Select
          className="screen-studio-create__select"
          showSearch
          placeholder={CREATE.STATUS_PLACEHOLDER}
          value={statusId}
          onChange={setStatusId}
          filterOption={(input, option) =>
            (option?.label || "").toLowerCase().indexOf(input.toLowerCase()) >=
            0
          }
          options={statuses.map((status) => ({
            value: status.id,
            label: `${status.order} · ${status.name}`,
          }))}
        />
      </div>

      <div className="m-bottom-16">
        <label className="input__label">{CREATE.NAME}</label>
        <AntInput
          className="screen-studio-create__field"
          placeholder={CREATE.NAME_PLACEHOLDER}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div className="screen-studio-create__generate m-bottom-16">
        <label className="input__label">{CREATE.GENERATE}</label>
        <AntInput
          className="screen-studio-create__field"
          placeholder={CREATE.INSTRUCTIONS_PLACEHOLDER}
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="screen-studio-create__file"
          onChange={handleUpload}
        />
        <Button
          className="m-top-8"
          loading={generating}
          disabled={creating}
          onClick={() => fileInputRef.current?.click()}
          icon={<MdOutlineImage />}
        >
          {CREATE.UPLOAD}
        </Button>
        <p className="screen-studio-create__hint">{CREATE.GENERATE_HINT}</p>
      </div>

      <div className="m-bottom-16">
        <label className="input__label">{CREATE.HTML}</label>
        <AntInput.TextArea
          rows={14}
          placeholder={CREATE.HTML_PLACEHOLDER}
          value={html}
          onChange={(e) => setHtml(e.target.value)}
        />
      </div>

      <div className="m-bottom-16">
        <label className="input__label">{CREATE.CSS}</label>
        <AntInput.TextArea
          rows={8}
          placeholder={CREATE.CSS_PLACEHOLDER}
          value={css}
          onChange={(e) => setCss(e.target.value)}
        />
      </div>

      <Button
        type="primary"
        onClick={handleCreate}
        loading={creating}
        disabled={!statusId || !html.trim()}
        icon={<MdOutlineUploadFile />}
      >
        {CREATE.SUBMIT}
      </Button>
      <p className="screen-studio-create__hint">{CREATE.HINT}</p>
    </div>
  );
};

const ImportTab = ({ statuses, navigate }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.SCREEN_STUDIO);
  const CREATE = t("CREATE", { returnObjects: true });
  const [stitchProjectId, setStitchProjectId] = useState("");
  const [stitchScreens, setStitchScreens] = useState(null);
  const [mappings, setMappings] = useState({});
  const [results, setResults] = useState(null);
  const [previewScreen, setPreviewScreen] = useState(null);

  const [fetchStitchScreens, { loading: loadingScreens }] = useLazyQuery(
    graphql.queries.UI_PROVIDER_SCREENS,
    {
      onCompleted: (data) => {
        setStitchScreens(data?.uiProviderScreens || []);
      },
    },
  );

  const [importScreens] = useMutation(
    graphql.mutations.IMPORT_UI_PROVIDER_SCREENS,
  );

  const [importingStatusId, setImportingStatusId] = useState(null);

  const handleFetch = () => {
    if (!stitchProjectId.trim()) return;
    fetchStitchScreens({
      variables: {
        provider: { project: { id: stitchProjectId.trim() } },
      },
    });
  };

  const handleSelectScreen = async (statusId, externalId) => {
    setMappings((prev) => ({ ...prev, [statusId]: externalId }));
    if (!externalId) return;

    setImportingStatusId(statusId);
    const screen = (stitchScreens || []).find(
      (s) => s.externalId === externalId,
    );

    try {
      const result = await importScreens({
        variables: {
          provider: { project: { id: stitchProjectId.trim() } },
          mappings: [
            {
              externalId,
              status: { id: statusId },
              htmlContent: screen && screen.htmlContent,
              name: screen && screen.name,
            },
          ],
        },
      });
      const entry = result?.data?.importUiProviderScreens?.[0];
      setResults((prev) => {
        const filtered = (prev || []).filter((r) => r.status?.id !== statusId);
        return [...filtered, entry];
      });
    } catch (e) {
      setResults((prev) => {
        const filtered = (prev || []).filter((r) => r.status?.id !== statusId);
        return [
          ...filtered,
          {
            status: { id: statusId, name: "" },
            error: e.message || "import_failed",
          },
        ];
      });
    }
    setImportingStatusId(null);
  };

  const screenOptions = (stitchScreens || []).map((s) => {
    const title = (s.name || "").split("/").pop() || s.externalId;
    return {
      value: s.externalId,
      label: `${title} - ${s.externalId}`,
      previewUrl: s.previewUrl,
    };
  });

  const columns = [
    {
      title: "Ordre",
      dataIndex: "order",
      width: 70,
    },
    {
      title: "Statut du process",
      dataIndex: "name",
    },
    {
      title: "Tag",
      dataIndex: "tag",
      width: 120,
      render: (tag) => (tag ? <Tag>{tag}</Tag> : "—"),
    },
    {
      title: "Écran Stitch",
      width: 350,
      render: (_, record) => (
        <Select
          showSearch
          virtual={false}
          listHeight={400}
          placeholder="Associer un écran"
          value={mappings[record.id] || undefined}
          filterOption={(input, option) => {
            const label = option?.label || option?.children?.toString() || "";
            return label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
          }}
          loading={importingStatusId === record.id}
          disabled={importingStatusId !== null}
          onChange={(value) => {
            if (value) {
              const screen = (stitchScreens || []).find(
                (s) => s.externalId === value,
              );
              if (screen) setPreviewScreen(screen);
            }
            handleSelectScreen(record.id, value);
          }}
          allowClear
          onClear={() =>
            setMappings((prev) => ({ ...prev, [record.id]: undefined }))
          }
          className="screen-studio-create__select-full"
          optionLabelProp="label"
        >
          {screenOptions.map((opt) => (
            <Select.Option key={opt.value} value={opt.value} label={opt.label}>
              <div className="d-flex align--center gap-8">
                {opt.previewUrl && (
                  <img
                    src={opt.previewUrl}
                    alt=""
                    className="screen-studio-create__option-thumb"
                  />
                )}
                <span>{opt.label}</span>
              </div>
            </Select.Option>
          ))}
        </Select>
      ),
    },
    {
      title: "Résultat",
      width: 200,
      render: (_, record) => {
        if (importingStatusId === record.id) {
          return <Tag color="blue">Import en cours...</Tag>;
        }
        const result = (results || []).find((r) => r.status?.id === record.id);
        if (!result) return null;
        if (result.error) return <Tag color="red">{result.error}</Tag>;
        if (result.screen) {
          return (
            <button
              type="button"
              className="screen-studio-create__link-btn"
              onClick={() =>
                navigate(
                  `${SCREENS.PRIVATE.ADMIN.SCREEN_STUDIOS.DETAIL.path}/${result.screen.id}`,
                )
              }
            >
              <Tag color="green">{result.status?.name}</Tag>
            </button>
          );
        }
        return null;
      },
    },
  ];

  return (
    <div>
      <div className="d-flex gap-8 m-bottom-16">
        <AntInput
          className="screen-studio-create__field"
          placeholder="Lien ou ID du projet Stitch"
          value={stitchProjectId}
          onChange={(e) => setStitchProjectId(e.target.value)}
          prefix={<MdOutlineLink />}
        />
        <Button
          onClick={handleFetch}
          loading={loadingScreens}
          disabled={!stitchProjectId.trim()}
        >
          Charger les écrans
        </Button>
      </div>

      {stitchScreens && (
        <div className="d-flex gap-16">
          <div className="screen-studio-create__import-table">
            <Table
              dataSource={statuses}
              columns={columns}
              rowKey="id"
              pagination={false}
              size="small"
            />
          </div>

          {previewScreen && previewScreen.previewUrl && (
            <div className="screen-studio-create__import-preview">
              <h4 className="screen-studio-create__preview-label">
                {CREATE.PREVIEW}
              </h4>
              <p className="screen-studio-create__preview-name">
                {previewScreen.name}
              </p>
              <img
                src={previewScreen.previewUrl}
                alt={previewScreen.name}
                className="screen-studio-create__preview-img"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Create;
